import { PositionWithIdentity } from "../../utils/positionIdentity";

/**
 * Stable signatures used to answer one question: has the server's answer
 * actually changed since the trader acted?
 *
 * Order-insensitive (sorted) so a backend that reorders rows does not read as a
 * change, and built only from fields the payload really carries — a signature
 * over invented fields would report change that did not happen.
 */

const positionSignature = (position: PositionWithIdentity): string =>
  [
    position.position_key || position.positionKey || "",
    position.instrument_id || position.instrument_name || "",
    position.side || "",
    position.margin_type || "",
    position.amount ?? "",
    position.avg_entry_price ?? "",
  ].join(":");

export const positionListSignature = (
  rows: readonly PositionWithIdentity[] | undefined
): string => (rows || []).map(positionSignature).sort().join("|");

const orderSignature = (order: any): string =>
  [
    order?.order_id ?? order?.orderId ?? order?.id ?? "",
    order?.order_status ?? order?.status ?? "",
    order?.filled ?? "",
    order?.amount ?? order?.quantity ?? "",
    order?.price ?? "",
  ].join(":");

export const orderListSignature = (
  orders: readonly unknown[] | undefined
): string => (orders || []).map(orderSignature).sort().join("|");

/**
 * Signature of the `/account` payload.
 *
 * Built only from fields the response actually declares — `equity`,
 * `available_balance`, `balance` and `positions` (see `GetAccount200Response`).
 * A signature over speculative fields would either never change or report
 * change that did not happen.
 */
export const accountSignature = (account: any): string =>
  [
    positionListSignature(account?.positions),
    account?.equity ?? "",
    account?.available_balance ?? "",
    account?.balance ?? "",
  ].join("|");

/**
 * Signature of the `/portfolio` payload.
 *
 * Fields verified against the live response: `balance`, `unrealized_pnl`,
 * `realized_pnl` and `user_margin.used` — the values a trade actually moves.
 * `win_rate` / `sharpe_ratio` are excluded: they are slow aggregates that may
 * not change on a single fill, so including them could never prove convergence.
 */
export const portfolioSignature = (portfolio: any): string =>
  [
    portfolio?.balance ?? "",
    portfolio?.unrealized_pnl ?? "",
    portfolio?.realized_pnl ?? "",
    portfolio?.user_margin?.used ?? "",
  ].join("|");

/**
 * Signature of the `/trade-history` payload — the set of trade ids. A fill adds
 * a row, which is the only evidence this list needs.
 */
export const tradeHistorySignature = (payload: any): string => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.trade_history || payload?.data || [];
  return list
    .map((t: any) => String(t?.trade_id ?? t?.id ?? ""))
    .sort()
    .join("|");
};
